<!-- eslint-disable no-console -->
<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import WppConnectAPI from './WppConnectAPI';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      imgSrc: '',
      messageShown: '',
      errorMessageShown: '',
      disabledTyping: false,
      apiToken: '',
      maxAttempts: 5,
      attempts: 0,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
  },
  validations: {
    inboxName: { required },
    phoneNumber: { required, isPhoneE164OrEmpty },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      
      // Make sure phone number is properly formatted in E.164 format
      const formattedPhoneNumber = this.phoneNumber.startsWith('+') 
        ? this.phoneNumber 
        : `+${this.phoneNumber.trim()}`;
      
      console.log('Creating channel with phone number:', formattedPhoneNumber);
      
      try {
        const whatsappChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.inboxName,
            channel: {
              type: 'whatsapp',
              phone_number: formattedPhoneNumber,
              provider: 'common_whatsapp',
              provider_config: {
                token: this.apiToken
              }
            },
          }
        );
        console.log('Channel created successfully:', whatsappChannel);
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: whatsappChannel.id,
          },
        });
      } catch (error) {
        console.error('Error creating channel:', error);
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
        );
      }
    },
    async generateQrCode(isRec = false) {
      if (!isRec) {
        if (this.disabledTyping) return;
        this.v$.$touch();
        if (this.v$.$invalid) {
          return;
        }
      }
      this.disabledTyping = true;
      this.messageShown = '';
      this.errorMessageShown = '';
      this.imgSrc = '';
      
      try {
        const { data } = await WppConnectAPI.getQrCode(
          this.phoneNumber,
          this.inboxName
        );
        
        await WppConnectAPI.waitInSeconds(2);
        if (data.success && data.qrcode) {
          const { qrcode } = data;
          this.imgSrc = qrcode.includes('data:image')
            ? qrcode
            : 'data:image/png;base64,' + qrcode;
          this.checkConnectionStatus();
        } else {
          if (this.attempts <= this.maxAttempts) {
            // eslint-disable-next-line no-plusplus
            this.attempts++;
            this.generateQrCode(true);
            return;
          }
          this.messageShown = this.$t('INBOX_MGMT.ADD.WHATSAPP.QRCODE_MESSAGES.FAILED_OBTAIN');
          this.disabledTyping = false;
        }
      } catch (error) {
        this.disabledTyping = false;
        this.errorMessageShown = error.response?.data?.message || 'Connection to WhatsApp API failed';
        this.messageShown = 'Please ensure the WPP Connect API is properly configured and running. Check your .env file for correct WPP_CONNECT_API_URL and WPP_CONNECT_SECRET_KEY values.';
      }
    },
    async checkConnectionStatus() {
      try {
        const { data } = await WppConnectAPI.checkConnectionStatus(
          this.phoneNumber,
          this.inboxName
        );
        
        const { qrcode } = data;
        if (
          data.success !== true ||
          ['QRCODE', 'INITIALIZING', 'CLOSED'].includes(data.status)
        ) {
          if (qrcode)
            this.imgSrc = qrcode.includes('data:image')
              ? qrcode
              : 'data:image/png;base64,' + qrcode;
          await WppConnectAPI.waitInSeconds(5);
          this.checkConnectionStatus();
          return;
        }
        this.messageShown = this.$t('INBOX_MGMT.ADD.WHATSAPP.QRCODE_MESSAGES.SUCCESS_ON_CONNECTION');
        this.imgSrc = '';
        this.apiToken = data.token;
        
        // Automatically create channel after successful authentication
        this.createChannel();
      } catch (error) {
        this.disabledTyping = false;
        this.errorMessageShown = error.response?.data?.message || 'Error checking connection status';
        this.messageShown = 'Please try again or check your WhatsApp API configuration.';
      }
    },
  },
};
</script>

<template>
  <form class="flex flex-wrap flex-col mx-0" @submit.prevent="generateQrCode()">
    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :disabled="disabledTyping"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
        <input
          v-model="phoneNumber"
          type="text"
          :disabled="disabledTyping"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
        </span>
      </label>
    </div>

    <div v-if="imgSrc" id="qr-code-container" class="flex-shrink-0 flex-grow-0">
      <img
        :src="imgSrc"
        alt="qr-code"
        style="width: 100%; max-width: 400px; margin-bottom: 8px;"
      />
    </div>

    <div v-if="errorMessageShown" class="flex-shrink-0 flex-grow-0 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
      <p class="font-bold">{{ errorMessageShown }}</p>
      <p>{{ messageShown }}</p>
    </div>
    <div v-else-if="messageShown" class="flex-shrink-0 flex-grow-0">
      <p>{{ messageShown }}</p>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <div v-if="!apiToken">
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.QRCODE_MESSAGES.GENERATE')"
          :is-loading="disabledTyping"
        />
        <span v-if="disabledTyping" class="spinner" />
      </div>
      
      <div v-if="apiToken" class="w-full">
        <NextButton
          type="button"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
          :is-loading="uiFlags.isCreating"
          @click="createChannel"
        />
      </div>
    </div>
  </form>
</template>
  